import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { GlobalAlertDialog, type GlobalAlertProps } from './GlobalAlertDialog';

type GlobalAlertContextValue = {
  openGlobalAlert: (props: GlobalAlertProps) => void;
  closeGlobalAlert: () => void;
};

const GlobalAlertContext = createContext<GlobalAlertContextValue>({
  openGlobalAlert: () => undefined,
  closeGlobalAlert: () => undefined,
});

export const useGlobalAlert = () => useContext(GlobalAlertContext);

type GlobalAlertState = (GlobalAlertProps & { isOpen: true }) | (Partial<GlobalAlertProps> & { isOpen: false });

export const GlobalAlertProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<GlobalAlertState>({ isOpen: false });

  const openGlobalAlert = useCallback((props: GlobalAlertProps) => {
    setState({ isOpen: true, ...props });
  }, []);

  const closeGlobalAlert = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const value = useMemo(() => ({ openGlobalAlert, closeGlobalAlert }), [openGlobalAlert, closeGlobalAlert]);
  return (
    <>
      <GlobalAlertContext.Provider value={value}>{children}</GlobalAlertContext.Provider>
      <GlobalAlertDialog {...state} close={closeGlobalAlert} />
    </>
  );
};
